import { slideDown, slideUp } from '../../../../src/js/utils/functions';

/**
 * Class Accordion
 */
class Accordion {
	transition: number;
	triggers: NodeListOf<HTMLButtonElement>;
	accordion: HTMLElement;

	constructor(element: HTMLElement) {
		this.accordion = element;
		this.triggers = this.accordion.querySelectorAll<HTMLButtonElement>('.block-bellow__top');
		this.transition = 400;

		this.triggers.forEach((trigger) => {
			trigger.setAttribute('role', 'button');
			trigger.setAttribute('tabindex', '0');
			trigger.setAttribute('aria-controls', trigger.id + '-content');

			// Set initial aria-expanded state based on presence of 'block-bellow--expanded'
			const isInitiallyExpanded = trigger
				.closest('.block-bellow')
				?.classList.contains('block-bellow--expanded');

			trigger.setAttribute('aria-expanded', String(isInitiallyExpanded));

			trigger.addEventListener('click', this.handleBellowClick.bind(this));

			trigger.addEventListener('keydown', this.handleBellowKeydown.bind(this));
		});
	}

	/**
	 * Handle bellow click
	 *
	 * @param {Event} event
	 */
	handleBellowClick(event: Event) {
		const target = event.currentTarget as HTMLButtonElement; // Cast to HTMLButtonElement

		const isExpanded = target
			.closest('.block-bellow')
			?.classList.contains('block-bellow--expanded');

		if (isExpanded) {
			this.collapseBellow(target);
		} else {
			this.expandBellow(target);
		}
	}

	/**
	 * Handle keyboard interactions for bellow triggers.
	 * Allows activation with Enter and Space keys.
	 *
	 * @param {KeyboardEvent} event
	 */
	handleBellowKeydown(event: KeyboardEvent) {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			this.handleBellowClick(event);
		}
	}

	/**
	 * Collapse Bellow
	 *
	 * @param {HTMLButtonElement} element Event target (the button that triggered the collapse)
	 */
	collapseBellow(element: HTMLButtonElement) {
		const bellowTrigger = element;

		bellowTrigger.setAttribute('aria-expanded', 'false');
		const bellowElement = bellowTrigger.closest('.block-bellow');
		if (bellowElement) {
			bellowElement.classList.remove('block-bellow--expanded');
			const collapsable = bellowElement.querySelector<HTMLElement>(
				'.block-bellow__collapsable'
			);
			if (collapsable) {
				collapsable.setAttribute('aria-hidden', 'true');

				slideUp(collapsable);
			}
		}
	}

	/**
	 * Expand Bellow
	 *
	 * @param {HTMLButtonElement} element Event target (the button that triggered the expansion)
	 */
	expandBellow(element: HTMLButtonElement) {
		const bellowTrigger = element;

		// Ensure you are collapsing a button if you are querying for a button.
		const expandedBellow = document.querySelector<HTMLButtonElement>(
			'.block-bellow--expanded .block-bellow__top'
		);

		// Collapse other bellow if exists and not multiple open by default.
		if (expandedBellow && document.querySelectorAll('.block-bellow--open-default').length < 2) {
			this.collapseBellow(expandedBellow);
		}

		// Expand bellow.
		bellowTrigger.setAttribute('aria-expanded', 'true');
		const bellowElement = bellowTrigger.closest('.block-bellow');
		if (bellowElement) {
			bellowElement.classList.add('block-bellow--expanded');
			const collapsable = bellowElement.querySelector<HTMLElement>(
				'.block-bellow__collapsable'
			);
			if (collapsable) {
				collapsable.setAttribute('aria-hidden', 'false');
				slideDown(collapsable);
			}
		}
	}
}

// Initialize the accordion on all elements with class 'block-accordion'
document.querySelectorAll('.block-accordion').forEach((accordion) => {
	new Accordion(accordion as HTMLElement);
});
